import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MeResponse } from "../api/me";
import { getMyAccess } from "../api/reports";
import { ApiError } from "../api/client";
import type { AccessMatrix, PageWithAccess, TenantBranding } from "../types";
import { VentureTopBar } from "../components/Chrome";

// E2 — venture hub. Lists the pages this end-user is granted.
export function VentureHub({
  branding,
  tenantSlug,
  me,
  isAdmin = false,
}: {
  branding: TenantBranding;
  tenantSlug: string;
  me: MeResponse;
  isAdmin?: boolean;
}) {
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setMatrix(await getMyAccess(tenantSlug));
      } catch (e) {
        setError(e instanceof ApiError ? e.detail : (e as Error).message);
      }
    })();
  }, [tenantSlug]);

  const granted: PageWithAccess[] = (matrix?.pages ?? []).filter((p) => p.granted);

  return (
    <div className="venture">
      <VentureTopBar
        branding={branding}
        me={me}
        grantedPages={granted}
        activePageId={null}
        isAdmin={isAdmin}
      />
      <div className="venture-body">
        <div className="venture-hub-header">
          <h1>{branding.application_name}</h1>
          <p style={{ color: "var(--ink-3)" }}>
            Welcome, {me.name ?? me.email.split("@")[0]}.
          </p>
        </div>
        {error && <div className="empty err">{error}</div>}
        {!error && matrix === null && <div className="empty">Loading pages…</div>}
        {!error && matrix !== null && granted.length === 0 && (
          <div className="empty">
            You don't have access to any pages yet. Ask your admin to grant you a page.
          </div>
        )}
        {granted.length > 0 && (
          <div className="hub-grid">
            {granted.map((p) => (
              <Link key={p.id} to={`/page/${p.id}`} className="hub-card">
                <div className="hub-card-name">{p.name}</div>
                {p.description && (
                  <div className="hub-card-desc">{p.description}</div>
                )}
                <div className="hub-card-meta">
                  {p.features.filter((f) => f.granted).length} features
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
